import androidx.compose.runtime.Composable
import androidx.compose.runtime.ProvidableCompositionLocal
import androidx.compose.runtime.compositionLocalOf
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.navigation.BasePath
import com.varabyte.kobweb.navigation.Router
import com.varabyte.kobweb.navigation.UpdateHistoryMode
import com.varabyte.kobweb.navigation.remove
import com.varabyte.kobweb.silk.defer.DeferringHost
import kotlin.Any
import kotlin.Unit
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.renderComposable
import mo.web.portfoliofront.components.layout.MarkdownLayout as _mo_web_portfoliofront_components_layout_MarkdownLayout
import mo.web.portfoliofront.components.layout.PageLayout as _mo_web_portfoliofront_components_layout_PageLayout
import mo.web.portfoliofront.pages.AboutPage as _mo_web_portfoliofront_pages_AboutPage
import mo.web.portfoliofront.pages.BlogPage as _mo_web_portfoliofront_pages_BlogPage
import mo.web.portfoliofront.pages.GamesPage as _mo_web_portfoliofront_pages_GamesPage
import mo.web.portfoliofront.pages.HomePage as _mo_web_portfoliofront_pages_HomePage
import mo.web.portfoliofront.pages.PhotosPage as _mo_web_portfoliofront_pages_PhotosPage
import mo.web.portfoliofront.pages.ProjectsPage as _mo_web_portfoliofront_pages_ProjectsPage
import mo.web.portfoliofront.pages.blog.Favoritegames2025Page as _mo_web_portfoliofront_pages_blog_Favoritegames2025Page
import mo.web.portfoliofront.pages.blog.YetanotherblogPage as _mo_web_portfoliofront_pages_blog_YetanotherblogPage

private val LayoutScopeLocal: ProvidableCompositionLocal<Any?> = compositionLocalOf<Any?> { null }

@Composable
private fun <T : Any> currentLayoutScope(): T {
    @Suppress("UNCHECKED_CAST")
    return LayoutScopeLocal.current as? T
        ?: error("Unexpected between Page/Layout scope and the parent Layout which it is providing it. Please report this issue to the Kobweb developers.")
}

@Composable
private fun <T : Any> provideLayoutScope(layoutScope: T, content: @Composable () -> Unit) {
    androidx.compose.runtime.CompositionLocalProvider(LayoutScopeLocal provides layoutScope, content = content)
}

public fun main() {
    AppGlobals.initialize(mapOf("title" to "Portfolio Front"))
    BasePath.set("")
    val router = Router()
    com.varabyte.kobweb.core.init.initKobweb(router) { ctx ->
        ctx.router.registerLayout(
            "mo.web.portfoliofront.components.layout.MarkdownLayout",
        ) { pageCtx, pageMethod -> 
            _mo_web_portfoliofront_components_layout_MarkdownLayout {
                pageMethod(pageCtx)
            }
        }
        ctx.router.registerLayout(
            "mo.web.portfoliofront.components.layout.PageLayout",
        ) { pageCtx, pageMethod -> 
            _mo_web_portfoliofront_components_layout_PageLayout(pageCtx) {
                provideLayoutScope(this) {
                    pageMethod(pageCtx)
                }
            }
        }
        ctx.router.register(
            "/",
            layoutId = "mo.web.portfoliofront.components.layout.PageLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.initHomePage(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_HomePage()
        }
        ctx.router.register(
            "/about",
            layoutId = "mo.web.portfoliofront.components.layout.PageLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.initAboutPage(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_AboutPage()
        }
        ctx.router.register(
            "/blog",
            layoutId = "mo.web.portfoliofront.components.layout.PageLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.initBlogPage(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_BlogPage()
        }
        ctx.router.register(
            "/blog/favoritegames2025",
            layoutId = "mo.web.portfoliofront.components.layout.MarkdownLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.blog.initFavoritegames2025Page(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_blog_Favoritegames2025Page()
        }
        ctx.router.register(
            "/blog/yetanotherblog",
            layoutId = "mo.web.portfoliofront.components.layout.MarkdownLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.blog.initYetanotherblogPage(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_blog_YetanotherblogPage()
        }
        ctx.router.register(
            "/games",
            layoutId = "mo.web.portfoliofront.components.layout.PageLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.initGamesPage(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_GamesPage()
        }
        ctx.router.register(
            "/photos",
            layoutId = "mo.web.portfoliofront.components.layout.PageLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.initPhotosPage(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_PhotosPage()
        }
        ctx.router.register(
            "/projects",
            layoutId = "mo.web.portfoliofront.components.layout.PageLayout",
            initRouteMethod = { ctx ->
                mo.web.portfoliofront.pages.initProjectsPage(ctx)
            },
        ) { pageCtx -> 
            _mo_web_portfoliofront_pages_ProjectsPage()
        }

    }
    router.addRouteInterceptor {
        path = path.removeSuffix(".html").removeSuffix(".htm")
    }

    com.varabyte.kobweb.silk.init.additionalSilkInitialization = { ctx ->
        com.varabyte.kobweb.silk.init.initSilkWidgets(ctx)
        com.varabyte.kobweb.silk.init.initSilkWidgetsKobweb(ctx)
        mo.web.portfoliofront.initStyles(ctx)
    }

    // Dedup any leading slashes after removing the origin, just in case someone typed
    // something like `https://site.com//about` by accident. If we pass `//about` into
    // `tryRoutingTo`, Kobweb will reject it as a protocol-relative URL; instead, we
    // want it to navigate to `/about`
    router.tryRoutingTo(BasePath.remove("/" + window.location.href.removePrefix(window.origin).trimStart('/')), UpdateHistoryMode.REPLACE)

    // For SEO, we may bake the contents of a page in at build time. However, we will
    // overwrite them the first time we render this page with their composable, dynamic
    // versions. Think of this as poor man's hydration :)
    // See also: https://en.wikipedia.org/wiki/Hydration_(web_development)
    val root = document.getElementById("_kobweb-root")!!
    while (root.firstChild != null) { root.removeChild(root.firstChild!!) }

    renderComposable(rootElementId = "_kobweb-root") {
        mo.web.portfoliofront.AppEntry {
            router.renderActivePage { DeferringHost { it() } }
        }
    }
}
